import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { filter } from 'rxjs'
import { tap } from 'rxjs/operators'
import { action, computed } from 'mobx'
import type { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import { task } from 'mobx-task'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { GetProgressionFn } from './ProgressionViewModel'
import { toStream } from '@/utils/mobx-util'
import type { DocumentsListViewModelFactory } from '@/features/docs'

/**
 * View model for draft reviewing.
 */
export class DraftReviewViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal for displaying draft approval.
   */
  readonly modal = new ModalViewModel()

  /**
   * Modal for displaying draft approval confirmation.
   */
  readonly confirmDraftApprovalModalVm = new ModalViewModel()

  /**
   * Modal for displaying draft rejection confirmation.
   */
  readonly confirmDraftRejectionModalVm = new ModalViewModel()

  /**
   * Documents list view model used to fetch draft returns.
   */
  readonly documentsListViewModel

  constructor(
    public readonly job: SubmittedJob,
    getProgression: GetProgressionFn,
    createDocumentsListViewModel: DocumentsListViewModelFactory,
    private readonly _progressionStore: ProgressionStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {
    this.documentsListViewModel = createDocumentsListViewModel({
      fetchAuthor: true,
      types: ['DRAFT_RETURN'],
      limit: 1,
      context: {
        type: 'Job',
        jobId: this.job.id,
      },
    })

    this._lifecycle.manage(this.documentsListViewModel)

    // Close draft approval modals when we're not in the draft approval step
    this._lifecycle.manage(
      toStream(() => getProgression()?.current, true).pipe(
        filter((s) => s !== 'DraftInReview'),
        tap(this._closeDraftApprovalModals),
      ),
    )
  }

  /**
   * The draft return document VM, if any.
   */
  @computed
  get draftReturnVm() {
    return this.documentsListViewModel.allDocuments.find(
      (d) => d.doc.typeId === 'DRAFT_RETURN',
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._closeDraftApprovalModals()
    this._lifecycle.deactivate()
  }

  /**
   * Shows the review dialog.
   */
  @action.bound
  reviewDraft() {
    void this.fetchDraftReturn(this._lifecycle.signal)
    this.modal.show()
  }

  /**
   * Fetches the draft return.
   */
  fetchDraftReturn = task(async (signal?: AbortSignal) => {
    await this.documentsListViewModel.fetch(signal)
  })

  /**
   * Approves the draft return.
   */
  approveDraft = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, 'DraftApproved')
      .catch(this._createSnackbarErrorHandler('Unable to approve the draft'))
    this._closeDraftApprovalModals()
  })

  /**
   * Rejects the draft return.
   */
  rejectDraft = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, 'DraftRejected')
      .catch(this._createSnackbarErrorHandler('Unable to reject the draft'))
    this._closeDraftApprovalModals()
  })

  /**
   * Closes all draft approval related modals.
   * @private
   */
  @action.bound
  private _closeDraftApprovalModals() {
    this.modal.close()
    this.confirmDraftApprovalModalVm.close()
    this.confirmDraftRejectionModalVm.close()
  }
}
